// ============================================================
// caseSearch.ts — the case list's client-side search.
//
// Extracted verbatim from CaseListScreen.tsx (dateSearchVariants + the
// per-case search blob) to keep that component smaller. One addition over
// the original: the displayed reference (SURNAME-YEAR-ID,
// utils/caseName.caseRef) is in the blob too, so typing the ref you see on
// a card finds it (the raw id was already there).
// ============================================================
import { caseName as caseDisplayName, caseRef } from './caseName'
import type { CaseSummary } from '../components/layout/CaseListScreen'

// Month names used by the date-search helper. UK convention is day-first,
// but the blob also includes ISO so users can paste a backend-style date.
const MONTH_NAMES_FULL = [
  'january', 'february', 'march', 'april', 'may', 'june',
  'july', 'august', 'september', 'october', 'november', 'december',
]
const MONTH_NAMES_SHORT = [
  'jan', 'feb', 'mar', 'apr', 'may', 'jun',
  'jul', 'aug', 'sep', 'oct', 'nov', 'dec',
]
// Builds a lowercase blob of every plausible way a user might type a given
// ISO date. The caller concatenates this with other searchable fields and
// runs a single .includes() check, so each variant just needs to be present
// as a substring — no parsing of the user's query required.
//
// We use getUTC* because backend date-only strings ("2025-10-15") are
// parsed as UTC midnight; using local accessors would shift the day in
// time zones west of UTC.
const dateSearchVariants = (isoDate: string | null | undefined): string => {
  if (!isoDate) return ''
  const d = new Date(isoDate)
  if (!Number.isFinite(d.getTime())) return ''
  const yyyy = String(d.getUTCFullYear())
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0')
  const dd = String(d.getUTCDate()).padStart(2, '0')
  const monthFull = MONTH_NAMES_FULL[d.getUTCMonth()]
  const monthShort = MONTH_NAMES_SHORT[d.getUTCMonth()]
  return [
    `${yyyy}-${mm}-${dd}`,    // ISO: 2025-10-15
    `${dd}/${mm}/${yyyy}`,    // UK:  15/10/2025
    `${dd}-${mm}-${yyyy}`,    // UK:  15-10-2025
    `${mm}/${yyyy}`,          //      10/2025
    `${mm}-${yyyy}`,          //      10-2025
    `${dd}/${mm}`,            //      15/10
    `${dd}-${mm}`,            //      15-10
    `${monthFull} ${yyyy}`,   //      october 2025
    `${monthShort} ${yyyy}`,  //      oct 2025
    monthFull,                //      october
    monthShort,               //      oct
    yyyy,                     //      2025
  ].join(' ').toLowerCase()
}

// True when `query` (already trimmed + lower-cased) matches the case. An
// empty query matches everything.
export const caseMatchesQuery = (c: CaseSummary, query: string): boolean => {
  if (!query) return true
  const forename = c.clientForename || c.client?.forename || ''
  const surname = c.clientSurname || c.client?.surname || ''
  const fullName = `${forename} ${surname}`.toLowerCase()
  const ni = (c.clientNiNumber || c.client?.niNumber || '').toLowerCase()
  const dob = c.clientDateOfBirth || c.client?.dateOfBirth
  const id = c.id.toLowerCase()
  const blob = [
    fullName,
    // The displayed case name (incl. "… v Defendant") is searchable too.
    caseDisplayName(c).toLowerCase(),
    caseRef(c).toLowerCase(),
    ni,
    id,
    dateSearchVariants(c.accidentDate),
    dateSearchVariants(dob),
  ].join(' ')
  return blob.includes(query)
}
